import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Users, Briefcase, Activity, Loader2, Trash2, Download, Plus, ExternalLink, Mail, X, CalendarDays } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { AppointmentsPanel } from '../components/crm/AppointmentsPanel';
import { CrmSlotPicker } from '../components/crm/CrmSlotPicker';
import DashboardShell from '../components/DashboardShell';
import { CRM_SOURCE_OPTIONS, sourceLabel } from '../utils/marketingContactSources';
import { resolveApiBase } from '../lib/http';

const EMPTY_LEAD = {
  first_name: '', last_name: '', email: '', phone: '',
  company: '', website: '', source: 'manual', notes: '',
};
const EMPTY_CONTACT = {
  first_name: '', last_name: '', email: '', phone: '',
  company: '', job_title: '', notes: '',
};
const EMPTY_BOOKING = {
  lead_id: '',
  customer_name: '',
  customer_email: '',
  customer_phone: '',
  start_time: '',
  end_time: '',
  timezone: '',
  notes: '',
};
const BOOKING_CREATE_TIMEOUT_MS = 20000;

const formatBookingDateTime = (value, timezone) => {
  if (!value) return '';
  const options = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  };
  try {
    return new Date(value).toLocaleString([], timezone ? { ...options, timeZone: timezone } : options);
  } catch {
    return new Date(value).toLocaleString([], options);
  }
};

// Simple CRM shell for per-account CRM under Marketing Suite
const CrmDashboard = ({ user }) => {
  const [searchParams] = useSearchParams();
  const initialWebsiteId = searchParams.get('website_id') || '';
  const leadFromUrl = searchParams.get('lead') || '';
  const [activeTab, setActiveTab] = useState(leadFromUrl ? 'leads' : 'contacts');
  const [pipeline, setPipeline] = useState({ stages: [], buckets: {}, totals: {} });
  const [pipelineLoading, setPipelineLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [deals, setDeals] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [leadDraft, setLeadDraft] = useState(EMPTY_LEAD);
  const [leadSaving, setLeadSaving] = useState(false);
  const [contactDraft, setContactDraft] = useState(EMPTY_CONTACT);
  const [contactSaving, setContactSaving] = useState(false);
  const [bookingModal, setBookingModal] = useState(false);
  const [bookingSaving, setBookingSaving] = useState(false);
  const [bookingDraft, setBookingDraft] = useState(EMPTY_BOOKING);

  // Send Campaign to Leads modal
  const [campaignModal, setCampaignModal] = useState(false);
  const [campaignSending, setCampaignSending] = useState(false);
  const [websites, setWebsites] = useState([]);
  const [workspaceWebsiteId, setWorkspaceWebsiteId] = useState(initialWebsiteId);
  const [campaignForm, setCampaignForm] = useState({
    website_id: '',
    campaign_name: 'CRM outreach',
    subject: 'A quick idea for your business',
    html_content: '<p>Hi there, we\'d love to help you grow online.</p>',
    band: '',
    min_score: '',
  });

  const token = localStorage.getItem('token');
  const API_URL = resolveApiBase().replace(/\/api\/?$/, '');

  const buildLeadsUrl = (extraParams = {}) => {
    const params = new URLSearchParams();
    if (workspaceWebsiteId) params.set('website_id', workspaceWebsiteId);
    Object.entries(extraParams).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    const qs = params.toString();
    return `${API_URL}/api/crm/leads${qs ? `?${qs}` : ''}`;
  };

  const buildCrmScopeUrl = (resource, extraParams = {}) => {
    const params = new URLSearchParams();
    if (workspaceWebsiteId) params.set('website_id', workspaceWebsiteId);
    Object.entries(extraParams).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    const qs = params.toString();
    return `${API_URL}/api/crm/${resource}${qs ? `?${qs}` : ''}`;
  };

  const currentWorkspace = websites.find((w) => w.id === workspaceWebsiteId) || null;

  const fetchWebsites = async () => {
    try {
      const res = await fetch(`${API_URL}/api/websites`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : (data.websites || []);
        setWebsites(list);
        if (list.length > 0 && !campaignForm.website_id) {
          setCampaignForm((f) => ({ ...f, website_id: list[0].id }));
        }
      }
    } catch (e) { /* silent */ }
  };

  const sendCampaignToLeads = async () => {
    setCampaignSending(true);
    try {
      const body = {
        website_id: campaignForm.website_id || '',
        crm_website_id: workspaceWebsiteId || '',
        campaign_name: campaignForm.campaign_name || 'CRM outreach',
        subject: campaignForm.subject || 'A quick idea for your business',
        html_content: campaignForm.html_content || '<p>Hi there!</p>',
      };
      if (campaignForm.band) body.band = campaignForm.band;
      if (campaignForm.min_score) body.min_score = parseInt(campaignForm.min_score, 10);
      const res = await fetch(`${API_URL}/api/crm/leads/to-campaign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      toast.success(`Draft campaign created for ${data.subscriber_count} leads. Go to ${campaignForm.website_id ? "the website's" : 'standalone'} Email Marketing to review & send.`);
      setCampaignModal(false);
    } catch (err) {
      toast.error(err.message || 'Failed to create campaign');
    } finally {
      setCampaignSending(false);
    }
  };

  const fetchCrmData = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      const [contactsRes, leadsRes, dealsRes, appointmentsRes] = await Promise.all([
        fetch(buildCrmScopeUrl('contacts'), { headers }).catch(() => null),
        fetch(buildLeadsUrl(), { headers }).catch(() => null),
        fetch(buildCrmScopeUrl('deals'), { headers }).catch(() => null),
        fetch(buildCrmScopeUrl('appointments'), { headers }).catch(() => null),
      ]);

      if (contactsRes && contactsRes.ok) setContacts(await contactsRes.json());
      if (leadsRes && leadsRes.ok) setLeads(await leadsRes.json());
      if (dealsRes && dealsRes.ok) setDeals(await dealsRes.json());
      if (appointmentsRes && appointmentsRes.ok) setAppointments(await appointmentsRes.json());
    } catch (err) {
      console.error('Failed to load CRM data', err);
      // Don't toast repeatedly; this shell is safe even if backend not ready
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWebsites();
  }, []);

  useEffect(() => {
    fetchCrmData();
  }, [workspaceWebsiteId]);

  useEffect(() => {
    if (!leadFromUrl) return;
    setActiveTab('leads');
    window.setTimeout(() => {
      const el = document.querySelector(`[data-lead-id="${CSS.escape(leadFromUrl)}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-2', 'ring-emerald-400');
      }
    }, 500);
  }, [leadFromUrl, leads]);

  const submitLead = async () => {
    const email = leadDraft.email.trim().toLowerCase();
    if (!email) {
      toast.error('Email is required');
      return;
    }
    setLeadSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/crm/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...leadDraft, email, website_id: workspaceWebsiteId || '' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Failed to create lead');
      setLeads((prev) => [data, ...prev]);
      setLeadDraft(EMPTY_LEAD);
      toast.success('Lead created');
    } catch (err) {
      toast.error(err.message || 'Failed to create lead');
    } finally {
      setLeadSaving(false);
    }
  };

  const submitContact = async () => {
    const email = contactDraft.email.trim().toLowerCase();
    if (!email) {
      toast.error('Email is required');
      return;
    }
    setContactSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/crm/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...contactDraft, email, website_id: workspaceWebsiteId || '' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Failed to create contact');
      setContacts((prev) => [data, ...prev]);
      setContactDraft(EMPTY_CONTACT);
      toast.success('Contact created');
    } catch (err) {
      toast.error(err.message || 'Failed to create contact');
    } finally {
      setContactSaving(false);
    }
  };

  const openBookingModal = (lead) => {
    const leadName = `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || lead.company || '';
    setBookingDraft({
      lead_id: lead.id,
      customer_name: leadName,
      customer_email: lead.email || '',
      customer_phone: lead.phone || '',
      start_time: '',
      end_time: '',
      timezone: '',
      notes: lead.notes || '',
    });
    setBookingModal(true);
  };

  const submitBooking = async () => {
    if (!bookingDraft.lead_id || !bookingDraft.customer_name || !bookingDraft.customer_email || !bookingDraft.start_time || !bookingDraft.end_time) {
      toast.error('Customer name, email, and an available time slot are required');
      return;
    }
    setBookingSaving(true);
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), BOOKING_CREATE_TIMEOUT_MS);
    try {
      const res = await fetch(`${API_URL}/api/crm/leads/${bookingDraft.lead_id}/appointments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        signal: controller.signal,
        body: JSON.stringify({
          customer_name: bookingDraft.customer_name,
          customer_email: bookingDraft.customer_email,
          customer_phone: bookingDraft.customer_phone,
          start_time: bookingDraft.start_time,
          end_time: bookingDraft.end_time,
          notes: bookingDraft.notes,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      if (data.calendar_status === 'synced') {
        toast.success('Appointment created and synced to Google Calendar');
      } else if (data.calendar_status === 'pending') {
        toast.success('Appointment created. Calendar and reminders are syncing in the background.');
      } else if (data.calendar_status === 'not_connected') {
        toast.success('Appointment created. Connect Google Calendar in Settings to sync invites.');
      } else {
        toast.success('Appointment created. Calendar sync needs attention.');
      }
      if (data.appointment) {
        setAppointments((prev) => [data.appointment, ...prev].sort((a, b) => new Date(a.start_time) - new Date(b.start_time)));
      }
      if (data.html_link) window.open(data.html_link, '_blank');
      setBookingModal(false);
      setBookingDraft(EMPTY_BOOKING);
      setActiveTab('appointments');
    } catch (err) {
      toast.error(
        err.name === 'AbortError'
          ? 'Appointment creation timed out. Please check the appointments list before trying again.'
          : (err.message || 'Failed to create appointment'),
      );
    } finally {
      window.clearTimeout(timer);
      setBookingSaving(false);
    }
  };

  const updateAppointment = async (appointmentId, payload) => {
    try {
      const res = await fetch(`${API_URL}/api/crm/appointments/${appointmentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      setAppointments((prev) => prev.map((item) => (item.id === appointmentId ? data : item)));
      if (payload.status === 'cancelled') {
        toast.success('Meeting cancelled — notifications sent to customer and owner when configured');
      } else if (payload.resend_notifications) {
        toast.success('Appointment updated and confirmations re-sent');
      } else {
        toast.success('Appointment updated');
      }
      return data;
    } catch (err) {
      toast.error(err.message || 'Failed to update appointment');
      return null;
    }
  };

  const cancelAppointment = (appointmentId) => updateAppointment(appointmentId, { status: 'cancelled' });

  const renderTab = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-20 text-zinc-400 text-sm">
          <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading CRM data...
        </div>
      );
    }

  const exportCsv = async (kind, filterSource) => {
    try {
      const url = kind === 'leads'
        ? (() => {
            const params = new URLSearchParams();
            if (workspaceWebsiteId) params.set('website_id', workspaceWebsiteId);
            if (filterSource) params.set('source', filterSource);
            const qs = params.toString();
            return `${API_URL}/api/crm/leads/export${qs ? `?${qs}` : ''}`;
          })()
        : (() => {
            const params = new URLSearchParams();
            if (workspaceWebsiteId) params.set('website_id', workspaceWebsiteId);
            const qs = params.toString();
            return `${API_URL}/api/crm/contacts/export${qs ? `?${qs}` : ''}`;
          })();
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement('a');
      const blobUrl = URL.createObjectURL(blob);
      a.href = blobUrl;
      const cd = res.headers.get('Content-Disposition') || '';
      const m = /filename="([^"]+)"/.exec(cd);
      a.download = m ? m[1] : `snagcart-${kind}.csv`;
      a.click();
      URL.revokeObjectURL(blobUrl);
      toast.success(`Exported ${kind} CSV`);
    } catch (e) {
      toast.error(`Export failed: ${e.message}`);
    }
  };

    if (activeTab === 'pipeline') {
      const loadPipeline = async () => {
        setPipelineLoading(true);
        try {
          const res = await fetch(buildCrmScopeUrl('pipeline/deals'), { headers: { Authorization: `Bearer ${token}` } });
          if (res.ok) setPipeline(await res.json());
        } finally { setPipelineLoading(false); }
      };
      const moveDealToStage = async (dealId, newStage) => {
        try {
          await fetch(`${API_URL}/api/crm/deals/${dealId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ stage: newStage, status: newStage === 'won' ? 'won' : newStage === 'lost' ? 'lost' : 'open' }),
          });
          toast.success(`Moved to ${newStage}`);
          loadPipeline();
        } catch (e) { toast.error(`Move failed: ${e.message}`); }
      };
      const STAGE_COLORS = {
        new: 'border-zinc-700', qualified: 'border-sky-500/30', proposal: 'border-violet-500/30',
        negotiation: 'border-amber-500/30', won: 'border-emerald-500/40', lost: 'border-red-500/30',
      };
      const STAGE_TEXT = {
        new: 'text-zinc-300', qualified: 'text-sky-300', proposal: 'text-violet-300',
        negotiation: 'text-amber-300', won: 'text-emerald-300', lost: 'text-red-300',
      };
      return (
        <div className="space-y-3" data-testid="crm-pipeline">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">Pipeline</h2>
              <p className="text-xs text-zinc-500">Drag opportunities through stages. Cards link back to the original Deal record.</p>
            </div>
            <button onClick={loadPipeline} disabled={pipelineLoading} data-testid="crm-pipeline-refresh"
              className="text-zinc-500 hover:text-zinc-200 disabled:opacity-50 inline-flex items-center gap-1 text-xs">
              {pipelineLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Activity className="w-3.5 h-3.5" />}
              {pipeline.stages.length === 0 ? 'Load pipeline' : 'Refresh'}
            </button>
          </div>
          {pipeline.stages.length === 0 && !pipelineLoading && (
            <p className="text-xs text-zinc-600 italic">Click &ldquo;Load pipeline&rdquo; to fetch your deal Kanban.</p>
          )}
          {pipeline.stages.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 overflow-x-auto" data-testid="crm-pipeline-grid">
              {pipeline.stages.map((stage) => {
                const items = pipeline.buckets[stage] || [];
                const total = pipeline.totals[stage] || 0;
                return (
                  <div key={stage}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { const id = e.dataTransfer.getData('text/plain'); if (id) moveDealToStage(id, stage); }}
                    data-testid={`crm-pipeline-col-${stage}`}
                    className={`bg-zinc-900/60 border-t-2 ${STAGE_COLORS[stage] || 'border-zinc-700'} rounded-lg p-2.5 min-h-[160px]`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-[10px] uppercase tracking-wider font-semibold capitalize ${STAGE_TEXT[stage] || 'text-zinc-400'}`}>{stage}</span>
                      <span className="text-[10px] text-zinc-500">{items.length} · ${total.toLocaleString()}</span>
                    </div>
                    <div className="space-y-1.5">
                      {items.map((d) => (
                        <div key={d.id} draggable
                          onDragStart={(e) => e.dataTransfer.setData('text/plain', d.id)}
                          data-testid={`crm-pipeline-card-${d.id.slice(0, 8)}`}
                          className="bg-zinc-800/60 border border-white/[0.04] rounded p-2 cursor-grab hover:border-violet-500/30 transition-colors">
                          <p className="text-xs font-medium text-white truncate">{d.name}</p>
                          {d.company && <p className="text-[10px] text-zinc-500 truncate">{d.company}</p>}
                          <div className="mt-1 flex items-center justify-between text-[10px]">
                            <span className="text-emerald-300">${(d.amount || 0).toLocaleString()}</span>
                            {d.expected_close_date && (
                              <span className="text-zinc-500">{new Date(d.expected_close_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
                            )}
                          </div>
                          <div className="mt-1.5 flex items-center justify-between gap-1">
                            <button
                              type="button"
                              data-testid={`crm-pipeline-view-deal-${d.id.slice(0, 8)}`}
                              className="text-[10px] text-violet-300 hover:text-violet-200 inline-flex items-center gap-0.5"
                              onClick={(e) => { e.stopPropagation(); setActiveTab('deals'); }}
                              title="Open this deal in the Deals tab"
                            >
                              <ExternalLink className="w-2.5 h-2.5" /> View deal
                            </button>
                            {d.contact_id && (
                              <button
                                type="button"
                                className="text-[10px] text-emerald-300 hover:text-emerald-200 inline-flex items-center gap-0.5"
                                onClick={(e) => { e.stopPropagation(); setActiveTab('contacts'); }}
                                title="Open the linked contact"
                              >
                                <Users className="w-2.5 h-2.5" /> Contact
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                      {items.length === 0 && (
                        <p className="text-[10px] text-zinc-600 italic text-center py-3">Drop here</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    if (activeTab === 'appointments') {
      return (
        <AppointmentsPanel
          appointments={appointments}
          loading={loading}
          saving={bookingSaving}
          onRefresh={fetchCrmData}
          onUpdateAppointment={updateAppointment}
          onCancelAppointment={cancelAppointment}
          token={token}
        />
      );
    }

  if (activeTab === 'contacts') {
      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Contacts</h2>
            <button
              type="button"
              onClick={() => exportCsv('contacts')}
              disabled={contacts.length === 0}
              data-testid="crm-export-contacts"
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium border border-emerald-500/30 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20 hover:border-emerald-500/50 disabled:opacity-40 disabled:cursor-not-allowed"
              title="Download all contacts as a CSV file (opens in Excel/Numbers/Sheets)"
            >
              <Download className="w-3 h-3" />
              Export CSV
            </button>
          </div>
          <p className="text-xs text-zinc-500 mb-2">
            Contacts are scoped to your selected workspace
            {workspaceWebsiteId && currentWorkspace ? ` (${currentWorkspace.name})` : ' (account-wide)'}.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-xs space-y-2" data-testid="crm-quick-add-contact">
              <h3 className="text-[13px] font-semibold mb-1">Quick add contact</h3>
              <p className="text-[11px] text-zinc-500 mb-1">
                Only email is required. Other fields are optional and can be filled later.
              </p>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="text"
                  placeholder="First name"
                  value={contactDraft.first_name}
                  onChange={(e) => setContactDraft({ ...contactDraft, first_name: e.target.value })}
                  data-testid="crm-contact-first-name"
                  className="h-8 rounded-md bg-zinc-950 border border-zinc-800 px-2 text-[11px] focus:outline-none focus:border-emerald-500"
                />
                <input
                  type="text"
                  placeholder="Last name"
                  value={contactDraft.last_name}
                  onChange={(e) => setContactDraft({ ...contactDraft, last_name: e.target.value })}
                  data-testid="crm-contact-last-name"
                  className="h-8 rounded-md bg-zinc-950 border border-zinc-800 px-2 text-[11px] focus:outline-none focus:border-emerald-500"
                />
                <input
                  type="email"
                  placeholder="Email *"
                  value={contactDraft.email}
                  onChange={(e) => setContactDraft({ ...contactDraft, email: e.target.value })}
                  data-testid="crm-contact-email"
                  className="col-span-2 h-8 rounded-md bg-zinc-950 border border-zinc-800 px-2 text-[11px] focus:outline-none focus:border-emerald-500"
                />
                <input
                  type="text"
                  placeholder="Phone"
                  value={contactDraft.phone}
                  onChange={(e) => setContactDraft({ ...contactDraft, phone: e.target.value })}
                  data-testid="crm-contact-phone"
                  className="h-8 rounded-md bg-zinc-950 border border-zinc-800 px-2 text-[11px] focus:outline-none focus:border-emerald-500"
                />
                <input
                  type="text"
                  placeholder="Company"
                  value={contactDraft.company}
                  onChange={(e) => setContactDraft({ ...contactDraft, company: e.target.value })}
                  data-testid="crm-contact-company"
                  className="h-8 rounded-md bg-zinc-950 border border-zinc-800 px-2 text-[11px] focus:outline-none focus:border-emerald-500"
                />
                <input
                  type="text"
                  placeholder="Job title"
                  value={contactDraft.job_title}
                  onChange={(e) => setContactDraft({ ...contactDraft, job_title: e.target.value })}
                  data-testid="crm-contact-job-title"
                  className="col-span-2 h-8 rounded-md bg-zinc-950 border border-zinc-800 px-2 text-[11px] focus:outline-none focus:border-emerald-500"
                />
              </div>
              <button
                type="button"
                onClick={submitContact}
                disabled={contactSaving || !contactDraft.email.trim()}
                data-testid="crm-contact-submit"
                className="w-full mt-1 h-8 rounded-md text-[11px] font-medium bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/40 text-emerald-200 disabled:opacity-40 inline-flex items-center justify-center gap-1"
              >
                {contactSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                Save contact
              </button>
            </div>

            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-xs text-zinc-400">
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-[12px]">Your contacts</span>
                <span className="text-[11px] text-zinc-500">
                  {contacts.length === 0
                    ? 'No contacts yet'
                    : `${contacts.length} contact${contacts.length > 1 ? 's' : ''}`}
                </span>
              </div>
              {contacts.length === 0 ? (
                <p className="text-[11px] text-zinc-500">Contacts you create will appear here.</p>
              ) : (
                <div className="space-y-1 max-h-64 overflow-auto pr-1">
                  {contacts.map((c) => (
                    <div
                      key={c.id}
                      className="flex items-center justify-between rounded-md border border-zinc-800 px-2 py-1"
                    >
                      <div>
                        <div className="text-[11px] font-semibold">
                          {c.first_name || c.last_name
                            ? `${c.first_name || ''} ${c.last_name || ''}`.trim()
                            : c.email}
                        </div>
                        <div className="text-[10px] text-zinc-500">{c.email}</div>
                        {c.company && (
                          <div className="text-[10px] text-zinc-500">{c.company}</div>
                        )}
                      </div>
                      <button
                        type="button"
                        className="text-[10px] text-zinc-500 hover:text-red-400 flex items-center gap-1"
                        onClick={async () => {
                          try {
                            const res = await fetch(`${API_URL}/api/crm/contacts/${c.id}`, {
                              method: 'DELETE',
                              headers: {
                                Authorization: `Bearer ${token}`,
                              },
                            });
                            if (!res.ok) {
                              const data = await res.json().catch(() => null);
                              throw new Error(data?.detail || 'Failed to delete contact');
                            }
                            setContacts((prev) => prev.filter((x) => x.id !== c.id));
                            toast.success('Contact deleted');
                          } catch (err) {
                            console.error(err);
                            toast.error(err.message || 'Failed to delete contact');
                          }
                        }}
                      >
                        <Trash2 className="w-3 h-3" />
                        Delete
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    if (activeTab === 'leads') {
      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Leads</h2>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => { fetchWebsites(); setCampaignModal(true); }}
                disabled={leads.length === 0}
                data-testid="crm-send-campaign"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium border border-pink-500/30 bg-pink-500/10 text-pink-200 hover:bg-pink-500/20 hover:border-pink-500/50 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Create a draft email campaign targeting all CRM leads"
              >
                <Mail className="w-3 h-3" />
                Send Campaign
              </button>
              <button
                type="button"
                onClick={() => exportCsv('leads', 'lead_scraper')}
                disabled={leads.length === 0}
                data-testid="crm-export-leads-scraper"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium border border-violet-500/30 bg-violet-500/10 text-violet-200 hover:bg-violet-500/20 hover:border-violet-500/50 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Download only leads imported from Lead Scraper"
              >
                <Download className="w-3 h-3" />
                Export Scraper leads
              </button>
              <button
                type="button"
                onClick={() => exportCsv('leads')}
                disabled={leads.length === 0}
                data-testid="crm-export-leads"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium border border-emerald-500/30 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20 hover:border-emerald-500/50 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Download all leads as CSV"
              >
                <Download className="w-3 h-3" />
                Export all
              </button>
            </div>
          </div>
          <p className="text-xs text-zinc-500 mb-2">
            Track potential customers before they become full contacts. Leads are scoped to your
            selected workspace{workspaceWebsiteId && currentWorkspace ? ` (${currentWorkspace.name})` : ' (account-wide)'}.
          </p>
          {workspaceWebsiteId ? (
            <div
              className="rounded-lg border border-sky-500/25 bg-sky-500/10 px-3 py-2 text-[11px] text-sky-100"
              data-testid="crm-leads-scope-banner"
            >
              Showing website-scoped leads only. Switch to <strong>Account-wide</strong> in the header to see account leads.
            </div>
          ) : (
            <div
              className="rounded-lg border border-violet-500/25 bg-violet-500/10 px-3 py-2 text-[11px] text-violet-100"
              data-testid="crm-leads-scope-banner"
            >
              Showing account-wide leads (including legacy imports). Pick a website workspace to isolate site-specific scrapes.
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-xs space-y-2" data-testid="crm-quick-add-lead">
              <h3 className="text-[13px] font-semibold mb-1">Quick add lead</h3>
              <p className="text-[11px] text-zinc-500 mb-1">
                Only email is required. Fill what you have — leads can be enriched later or converted to a deal.
              </p>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="text"
                  placeholder="First name"
                  value={leadDraft.first_name}
                  onChange={(e) => setLeadDraft({ ...leadDraft, first_name: e.target.value })}
                  data-testid="crm-lead-first-name"
                  className="h-8 rounded-md bg-zinc-950 border border-zinc-800 px-2 text-[11px] focus:outline-none focus:border-emerald-500"
                />
                <input
                  type="text"
                  placeholder="Last name"
                  value={leadDraft.last_name}
                  onChange={(e) => setLeadDraft({ ...leadDraft, last_name: e.target.value })}
                  data-testid="crm-lead-last-name"
                  className="h-8 rounded-md bg-zinc-950 border border-zinc-800 px-2 text-[11px] focus:outline-none focus:border-emerald-500"
                />
                <input
                  type="email"
                  placeholder="Email *"
                  value={leadDraft.email}
                  onChange={(e) => setLeadDraft({ ...leadDraft, email: e.target.value })}
                  data-testid="crm-lead-email"
                  className="col-span-2 h-8 rounded-md bg-zinc-950 border border-zinc-800 px-2 text-[11px] focus:outline-none focus:border-emerald-500"
                />
                <input
                  type="text"
                  placeholder="Phone"
                  value={leadDraft.phone}
                  onChange={(e) => setLeadDraft({ ...leadDraft, phone: e.target.value })}
                  data-testid="crm-lead-phone"
                  className="h-8 rounded-md bg-zinc-950 border border-zinc-800 px-2 text-[11px] focus:outline-none focus:border-emerald-500"
                />
                <input
                  type="text"
                  placeholder="Company"
                  value={leadDraft.company}
                  onChange={(e) => setLeadDraft({ ...leadDraft, company: e.target.value })}
                  data-testid="crm-lead-company"
                  className="h-8 rounded-md bg-zinc-950 border border-zinc-800 px-2 text-[11px] focus:outline-none focus:border-emerald-500"
                />
                <input
                  type="text"
                  placeholder="Website (https://…)"
                  value={leadDraft.website}
                  onChange={(e) => setLeadDraft({ ...leadDraft, website: e.target.value })}
                  data-testid="crm-lead-website"
                  className="col-span-2 h-8 rounded-md bg-zinc-950 border border-zinc-800 px-2 text-[11px] focus:outline-none focus:border-emerald-500"
                />
                <select
                  value={leadDraft.source}
                  onChange={(e) => setLeadDraft({ ...leadDraft, source: e.target.value })}
                  data-testid="crm-lead-source"
                  className="h-8 rounded-md bg-zinc-950 border border-zinc-800 px-2 text-[11px] focus:outline-none focus:border-emerald-500"
                >
                  {CRM_SOURCE_OPTIONS.filter((opt) => opt.value).map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="Notes / address"
                  value={leadDraft.notes}
                  onChange={(e) => setLeadDraft({ ...leadDraft, notes: e.target.value })}
                  data-testid="crm-lead-notes"
                  className="h-8 rounded-md bg-zinc-950 border border-zinc-800 px-2 text-[11px] focus:outline-none focus:border-emerald-500"
                />
              </div>
              <button
                type="button"
                onClick={submitLead}
                disabled={leadSaving || !leadDraft.email.trim()}
                data-testid="crm-lead-submit"
                className="w-full mt-1 h-8 rounded-md text-[11px] font-medium bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/40 text-emerald-200 disabled:opacity-40 inline-flex items-center justify-center gap-1"
              >
                {leadSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                Save lead
              </button>
            </div>

            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-xs text-zinc-400">
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-[12px]">Your leads</span>
                <span className="text-[11px] text-zinc-500">
                  {leads.length === 0
                    ? 'No leads yet'
                    : `${leads.length} lead${leads.length > 1 ? 's' : ''}`}
                </span>
              </div>
              {leads.length === 0 ? (
                <p className="text-[11px] text-zinc-500">Leads you create will appear here.</p>
              ) : (
                <div className="max-h-64 overflow-auto pr-1 border border-zinc-800 rounded-md">
                  <table className="w-full text-[11px]">
                    <thead className="bg-zinc-900/80 border-b border-zinc-800">
                      <tr>
                        <th className="px-2 py-1 text-left">Email</th>
                        <th className="px-2 py-1 text-left">Company</th>
                        <th className="px-2 py-1 text-left">Phone</th>
                        <th className="px-2 py-1 text-left">Website</th>
                        <th className="px-2 py-1 text-left">Source</th>
                        <th className="px-2 py-1 text-left">Address</th>
                        <th className="px-2 py-1 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leads.map((l) => (
                        <tr
                          key={l.id}
                          data-lead-id={l.id}
                          className="border-b border-zinc-800/60 hover:bg-zinc-900/60"
                        >
                          <td className="px-2 py-1 align-top text-zinc-100 break-all">{l.email}</td>
                          <td className="px-2 py-1 align-top text-zinc-300">{l.company || '-'}</td>
                          <td className="px-2 py-1 align-top text-zinc-300">{l.phone || '-'}</td>
                          <td className="px-2 py-1 align-top text-emerald-300">
                            {l.website ? (
                              <a
                                href={l.website}
                                target="_blank"
                                rel="noreferrer"
                                className="hover:underline"
                              >
                                {l.website}
                              </a>
                            ) : (
                              <span className="text-zinc-400">-</span>
                            )}
                          </td>
                          <td className="px-2 py-1 align-top text-zinc-300">{sourceLabel(l.source)}</td>
                          <td className="px-2 py-1 align-top text-zinc-400">
                            {(() => {
                              const isLlmJunk = (v) => {
                                const s = String(v || '').trim();
                                if (!s) return false;
                                const low = s.toLowerCase();
                                return s.startsWith('[LLM Error:') || s.startsWith('[LLM ERROR]')
                                  || low.includes('insufficient_quota') || low.includes('rate_limit');
                              };
                              const mapsAddr = String(l.address || '').trim();
                              if (mapsAddr && !isLlmJunk(mapsAddr)) return mapsAddr;
                              // Legacy scrapers stored Maps address in notes; pipeline notes are AI summaries.
                              const notes = String(l.notes || '').trim();
                              if (l.pipeline_version === 'lead-v2' || isLlmJunk(notes)) return '-';
                              return notes || '-';
                            })()}
                          </td>
                          <td className="px-2 py-1 align-top text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                type="button"
                                data-testid={`crm-lead-report-${l.id.slice(0,8)}`}
                                className="text-[10px] text-sky-300 hover:text-sky-200 inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-sky-500/30 hover:bg-sky-500/15"
                                onClick={async () => {
                                  try {
                                    const res = await fetch(`${API_URL}/api/crm/leads/${l.id}/report`, {
                                      method: 'POST',
                                      headers: { Authorization: `Bearer ${token}` },
                                    });
                                    const data = await res.json().catch(() => ({}));
                                    if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
                                    // Fetch the PDF with auth and open it in a new tab.
                                    const pdf = await fetch(`${API_URL}${data.report_url}`, { headers: { Authorization: `Bearer ${token}` } });
                                    const blob = await pdf.blob();
                                    window.open(URL.createObjectURL(blob), '_blank');
                                    toast.success('Growth report generated');
                                  } catch (err) { toast.error(err.message); }
                                }}
                                title="Generate a branded PDF growth-opportunity report for this lead"
                              >
                                Report
                              </button>
                              <button
                                type="button"
                                data-testid={`crm-lead-preview-${l.id.slice(0,8)}`}
                                className="text-[10px] text-emerald-300 hover:text-emerald-200 inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-emerald-500/30 hover:bg-emerald-500/15"
                                onClick={async () => {
                                  try {
                                    const res = await fetch(`${API_URL}/api/crm/leads/${l.id}/preview-site`, {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                      body: JSON.stringify({}),
                                    });
                                    const data = await res.json().catch(() => ({}));
                                    if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
                                    window.open(`${API_URL}/api/sites/${data.slug}/html`, '_blank');
                                    toast.success('Preview site built');
                                  } catch (err) { toast.error(err.message); }
                                }}
                                title="Auto-build + publish a preview website for this lead"
                              >
                                Preview
                              </button>
                              <button
                                type="button"
                                data-testid={`crm-lead-qualify-${l.id.slice(0,8)}`}
                                className="text-[10px] text-amber-300 hover:text-amber-200 inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-amber-500/30 hover:bg-amber-500/15"
                                onClick={async () => {
                                  try {
                                    const res = await fetch(`${API_URL}/api/crm/leads/${l.id}/qualify`, {
                                      method: 'POST',
                                      headers: { Authorization: `Bearer ${token}` },
                                    });
                                    const data = await res.json().catch(() => ({}));
                                    if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
                                    toast.success(`BANT ${data.bant_score}/100 — ${data.status}`);
                                    fetchCrmData();
                                  } catch (err) { toast.error(err.message); }
                                }}
                                title="Run the BANT qualifier on this lead"
                              >
                                Qualify
                              </button>
                              <button
                                type="button"
                                data-testid={`crm-lead-book-${l.id.slice(0,8)}`}
                                className="text-[10px] text-teal-300 hover:text-teal-200 inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-teal-500/30 hover:bg-teal-500/15"
                                onClick={() => {
                                  if (!l.email) {
                                    toast.error('This lead needs an email before you can book an appointment.');
                                    return;
                                  }
                                  openBookingModal(l);
                                }}
                                title="Create an appointment directly for this lead"
                              >
                                Book
                              </button>
                              {l.status !== 'converted' && (
                                <button
                                  type="button"
                                  data-testid={`crm-lead-convert-${l.id.slice(0,8)}`}
                                  className="text-[10px] text-violet-300 hover:text-violet-200 inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-violet-500/30 hover:bg-violet-500/15"
                                  onClick={async () => {
                                    const amt = window.prompt('Expected deal amount in $ (leave blank for 0):', '5000');
                                    if (amt === null) return;
                                    try {
                                      const res = await fetch(`${API_URL}/api/crm/leads/${l.id}/convert`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                        body: JSON.stringify({ amount: parseFloat(amt) || 0 }),
                                      });
                                      const data = await res.json().catch(() => ({}));
                                      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
                                      toast.success(data.already_converted ? 'Already converted — opening deal' : 'Converted to opportunity!');
                                      fetchCrmData();
                                    } catch (err) { toast.error(err.message); }
                                  }}
                                  title="Convert this lead into a sales opportunity"
                                >
                                  → Convert
                                </button>
                              )}
                              <button
                                type="button"
                                className="text-[10px] text-zinc-500 hover:text-red-400 inline-flex items-center gap-1"
                                onClick={async () => {
                                  try {
                                    const res = await fetch(`${API_URL}/api/crm/leads/${l.id}`, {
                                      method: 'DELETE',
                                      headers: { Authorization: `Bearer ${token}` },
                                    });
                                    if (!res.ok) {
                                      const data = await res.json().catch(() => null);
                                      throw new Error(data?.detail || 'Failed to delete lead');
                                    }
                                    setLeads((prev) => prev.filter((x) => x.id !== l.id));
                                    toast.success('Lead deleted');
                                  } catch (err) {
                                    console.error(err);
                                    toast.error(err.message || 'Failed to delete lead');
                                  }
                                }}
                              >
                                <Trash2 className="w-3 h-3" />
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    if (activeTab === 'deals') {
      return (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">Deals</h2>
          <p className="text-xs text-zinc-500 mb-2">
            Track revenue opportunities for your selected workspace
            {workspaceWebsiteId && currentWorkspace ? ` (${currentWorkspace.name})` : ' (account-wide)'}.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-xs space-y-2">
              <h3 className="text-[13px] font-semibold mb-1">Quick add deal</h3>
              <div className="space-y-2 mt-1">
                <input
                  id="crm-quick-deal-name"
                  type="text"
                  placeholder="Deal name (e.g. Website project)"
                  className="w-full h-8 rounded-md bg-zinc-950 border border-zinc-800 px-2 text-[11px] focus:outline-none focus:border-emerald-500 mb-1"
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const input = e.currentTarget;
                      const name = input.value.trim();
                      if (!name) {
                        toast.error('Deal name is required');
                        return;
                      }
                      try {
                        const res = await fetch(`${API_URL}/api/crm/deals`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`,
                          },
                          body: JSON.stringify({ name, website_id: workspaceWebsiteId || '' }),
                        });
                        const data = await res.json();
                        if (!res.ok) {
                          throw new Error(data?.detail || 'Failed to create deal');
                        }
                        input.value = '';
                        setDeals((prev) => [data, ...prev]);
                        toast.success('Deal created');
                      } catch (err) {
                        console.error(err);
                        toast.error(err.message || 'Failed to create deal');
                      }
                    }
                  }}
                />
              </div>
            </div>

            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-xs text-zinc-400">
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-[12px]">Your deals</span>
                <span className="text-[11px] text-zinc-500">
                  {deals.length === 0
                    ? 'No deals yet'
                    : `${deals.length} deal${deals.length > 1 ? 's' : ''}`}
                </span>
              </div>
              {deals.length === 0 ? (
                <p className="text-[11px] text-zinc-500">Deals you create will appear here.</p>
              ) : (
                <div className="space-y-1 max-h-64 overflow-auto pr-1">
                  {deals.map((d) => (
                    <div
                      key={d.id}
                      className="flex items-center justify-between rounded-md border border-zinc-800 px-2 py-1"
                    >
                      <div>
                        <div className="text-[11px] font-semibold">{d.name}</div>
                        {d.amount != null && (
                          <div className="text-[10px] text-zinc-500">
                            {d.amount} {d.currency || 'USD'}
                          </div>
                        )}
                        <div className="text-[10px] text-zinc-500 capitalize">
                          {d.status || 'open'}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="text-[10px] text-zinc-500 hover:text-red-400 flex items-center gap-1"
                        onClick={async () => {
                          try {
                            const res = await fetch(`${API_URL}/api/crm/deals/${d.id}`, {
                              method: 'DELETE',
                              headers: {
                                Authorization: `Bearer ${token}`,
                              },
                            });
                            if (!res.ok) {
                              const data = await res.json().catch(() => null);
                              throw new Error(data?.detail || 'Failed to delete deal');
                            }
                            setDeals((prev) => prev.filter((x) => x.id !== d.id));
                            toast.success('Deal deleted');
                          } catch (err) {
                            console.error(err);
                            toast.error(err.message || 'Failed to delete deal');
                          }
                        }}
                      >
                        <Trash2 className="w-3 h-3" />
                        Delete
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <h2 className="text-lg font-semibold">Overview</h2>
        <p className="text-xs text-zinc-500">
          Use the tabs on the left to view Contacts, Leads, and Deals. More CRM features like
          activities, automation, and reports will be added here.
        </p>
      </div>
    );
  };

  return (
    <DashboardShell
      user={user}
      title="Contacts & CRM"
      subtitle="Manage leads, contacts, deals, and appointments"
      activeNav="crm"
      fullscreen
      backPath="/dashboard"
      headerExtras={(
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-zinc-500 hidden sm:inline">Workspace</span>
          <select
            value={workspaceWebsiteId}
            onChange={(e) => setWorkspaceWebsiteId(e.target.value)}
            data-testid="crm-workspace-select"
            className="h-8 rounded-md bg-zinc-950 border border-zinc-700 px-2 text-[11px] text-zinc-200 focus:outline-none focus:border-emerald-500 min-w-[140px]"
          >
            <option value="">Account-wide</option>
            {websites.map((w) => (
              <option key={w.id} value={w.id}>{w.name || w.id}</option>
            ))}
          </select>
        </div>
      )}
    >
      <CampaignModal
        open={campaignModal}
        onClose={() => setCampaignModal(false)}
        form={campaignForm}
        setForm={setCampaignForm}
        websites={websites}
        onSend={sendCampaignToLeads}
        sending={campaignSending}
      />
      <BookingModal
        open={bookingModal}
        onClose={() => setBookingModal(false)}
        draft={bookingDraft}
        setDraft={setBookingDraft}
        onSave={submitBooking}
        saving={bookingSaving}
        token={token}
      />

      <main className="h-full overflow-y-auto">
        <div className="max-w-7xl mx-auto p-6 flex gap-6">
        <aside className="w-56 bg-zinc-900/60 border border-zinc-800 rounded-xl p-3 space-y-1" data-testid="crm-sidebar">
          <div className="px-2 py-1.5 mb-2 border-b border-zinc-800 grid grid-cols-4 gap-1 text-[10px] text-zinc-400" data-testid="crm-summary">
            <div className="flex flex-col items-center">
              <span className="text-emerald-300 text-sm font-semibold">{contacts.length}</span>
              <span className="uppercase tracking-wider">Contacts</span>
            </div>
            <div className="flex flex-col items-center">
              <span className="text-sky-300 text-sm font-semibold">{leads.length}</span>
              <span className="uppercase tracking-wider">Leads</span>
            </div>
            <div className="flex flex-col items-center">
              <span className="text-violet-300 text-sm font-semibold">{deals.length}</span>
              <span className="uppercase tracking-wider">Deals</span>
            </div>
            <div className="flex flex-col items-center">
              <span className="text-teal-300 text-sm font-semibold">{appointments.length}</span>
              <span className="uppercase tracking-wider">Meetings</span>
            </div>
          </div>
          <button
            className={`flex items-center gap-2 w-full px-3 py-2 rounded-lg text-xs ${
              activeTab === 'contacts'
                ? 'bg-emerald-500/20 text-emerald-400'
                : 'text-zinc-400 hover:bg-zinc-800 hover:text-white'
            }`}
            onClick={() => setActiveTab('contacts')}
          >
            <Users className="w-4 h-4" /> Contacts
          </button>
          <button
            className={`flex items-center gap-2 w-full px-3 py-2 rounded-lg text-xs ${
              activeTab === 'leads'
                ? 'bg-emerald-500/20 text-emerald-400'
                : 'text-zinc-400 hover:bg-zinc-800 hover:text-white'
            }`}
            onClick={() => setActiveTab('leads')}
          >
            <Briefcase className="w-4 h-4" /> Leads
          </button>
          <button
            className={`flex items-center gap-2 w-full px-3 py-2 rounded-lg text-xs ${
              activeTab === 'deals'
                ? 'bg-emerald-500/20 text-emerald-400'
                : 'text-zinc-400 hover:bg-zinc-800 hover:text-white'
            }`}
            onClick={() => setActiveTab('deals')}
          >
            <Briefcase className="w-4 h-4" /> Deals
          </button>
          <button
            className={`flex items-center gap-2 w-full px-3 py-2 rounded-lg text-xs ${
              activeTab === 'pipeline'
                ? 'bg-violet-500/20 text-violet-300'
                : 'text-zinc-400 hover:bg-zinc-800 hover:text-white'
            }`}
            onClick={() => setActiveTab('pipeline')}
            data-testid="crm-nav-pipeline"
          >
            <Activity className="w-4 h-4" /> Pipeline
          </button>
          <button
            className={`flex items-center gap-2 w-full px-3 py-2 rounded-lg text-xs ${
              activeTab === 'appointments'
                ? 'bg-teal-500/20 text-teal-300'
                : 'text-zinc-400 hover:bg-zinc-800 hover:text-white'
            }`}
            onClick={() => setActiveTab('appointments')}
            data-testid="crm-nav-appointments"
          >
            <CalendarDays className="w-4 h-4" /> Appointments
          </button>
        </aside>

        <section className="flex-1">
          {renderTab()}
        </section>
        </div>
      </main>
    </DashboardShell>
  );
};

// ── Send Campaign Modal ──────────────────────────────────────────────────────
const CampaignModal = ({ open, onClose, form, setForm, websites, onSend, sending }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="w-full max-w-lg bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-pink-500/20 flex items-center justify-center">
              <Mail className="w-4 h-4 text-pink-300" />
            </div>
            <div>
              <h2 className="text-sm font-semibold">Send Campaign to Leads</h2>
              <p className="text-[11px] text-zinc-500">Creates a draft email campaign targeting your CRM leads.</p>
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3 text-xs">
          <div>
            <label className="block text-zinc-400 mb-1">Website <span className="text-zinc-600">(optional)</span></label>
            <select
              value={form.website_id}
              onChange={(e) => setForm((f) => ({ ...f, website_id: e.target.value }))}
              className="w-full h-8 rounded-md bg-zinc-950 border border-zinc-700 px-2 text-[11px] focus:outline-none focus:border-pink-500"
            >
              <option value="">No website (standalone marketing)</option>
              {websites.map((w) => (
                <option key={w.id} value={w.id}>{w.name || w.domain || w.id}</option>
              ))}
            </select>
            <p className="text-[10px] text-zinc-600 mt-0.5">{form.website_id ? 'The campaign will be created under this website&apos;s Email Marketing.' : 'The campaign will be created in standalone Email Marketing (no website needed).'}</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-zinc-400 mb-1">Filter by score band</label>
              <select
                value={form.band}
                onChange={(e) => setForm((f) => ({ ...f, band: e.target.value }))}
                className="w-full h-8 rounded-md bg-zinc-950 border border-zinc-700 px-2 text-[11px] focus:outline-none focus:border-pink-500"
              >
                <option value="">All leads</option>
                <option value="hot">Hot only</option>
                <option value="warm">Warm only</option>
                <option value="cold">Cold only</option>
              </select>
            </div>
            <div>
              <label className="block text-zinc-400 mb-1">Min BANT score</label>
              <input
                type="number"
                min="0" max="100"
                placeholder="e.g. 50"
                value={form.min_score}
                onChange={(e) => setForm((f) => ({ ...f, min_score: e.target.value }))}
                className="w-full h-8 rounded-md bg-zinc-950 border border-zinc-700 px-2 text-[11px] focus:outline-none focus:border-pink-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-zinc-400 mb-1">Campaign name</label>
            <input
              type="text"
              value={form.campaign_name}
              onChange={(e) => setForm((f) => ({ ...f, campaign_name: e.target.value }))}
              className="w-full h-8 rounded-md bg-zinc-950 border border-zinc-700 px-2 text-[11px] focus:outline-none focus:border-pink-500"
            />
          </div>

          <div>
            <label className="block text-zinc-400 mb-1">Email subject</label>
            <input
              type="text"
              value={form.subject}
              onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
              className="w-full h-8 rounded-md bg-zinc-950 border border-zinc-700 px-2 text-[11px] focus:outline-none focus:border-pink-500"
            />
          </div>

          <div>
            <label className="block text-zinc-400 mb-1">Email body (HTML)</label>
            <textarea
              rows={4}
              value={form.html_content}
              onChange={(e) => setForm((f) => ({ ...f, html_content: e.target.value }))}
              className="w-full rounded-md bg-zinc-950 border border-zinc-700 px-2 py-1.5 text-[11px] focus:outline-none focus:border-pink-500 font-mono resize-none"
            />
            <p className="text-[10px] text-zinc-600 mt-0.5">Use {'{{first_name}}'}, {'{{company}}'} as placeholders.</p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-md text-xs border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500"
          >
            Cancel
          </button>
          <button
            onClick={onSend}
            disabled={sending}
            className="px-4 py-1.5 rounded-md text-xs font-medium bg-pink-500/20 border border-pink-500/40 text-pink-200 hover:bg-pink-500/30 disabled:opacity-40 inline-flex items-center gap-1.5"
          >
            {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
            {sending ? 'Creating…' : 'Create Draft Campaign'}
          </button>
        </div>
      </div>
    </div>
  );
};

const BookingModal = ({ open, onClose, draft, setDraft, onSave, saving, token }) => {
  if (!open) return null;
  const selectedLabel = draft.start_time
    ? `${formatBookingDateTime(draft.start_time, draft.timezone)}${draft.timezone ? ` ${draft.timezone}` : ''}`
    : '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="crm-booking-modal">
      <div className="w-full max-w-lg bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold">Create appointment</h2>
            <p className="text-[11px] text-zinc-500">This books directly in CRM and syncs to Google Calendar when connected.</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="crm-booking-close-button">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3 text-xs">
          <div>
            <label className="block text-zinc-400 mb-1">Customer name</label>
            <input value={draft.customer_name} onChange={(e) => setDraft((d) => ({ ...d, customer_name: e.target.value }))} className="w-full h-9 rounded-md bg-zinc-950 border border-zinc-700 px-3 text-[11px]" data-testid="crm-booking-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-zinc-400 mb-1">Email</label>
              <input type="email" value={draft.customer_email} onChange={(e) => setDraft((d) => ({ ...d, customer_email: e.target.value }))} className="w-full h-9 rounded-md bg-zinc-950 border border-zinc-700 px-3 text-[11px]" data-testid="crm-booking-email-input" />
            </div>
            <div>
              <label className="block text-zinc-400 mb-1">Phone</label>
              <input value={draft.customer_phone} onChange={(e) => setDraft((d) => ({ ...d, customer_phone: e.target.value }))} className="w-full h-9 rounded-md bg-zinc-950 border border-zinc-700 px-3 text-[11px]" data-testid="crm-booking-phone-input" />
            </div>
          </div>
          <div>
            <label className="block text-zinc-400 mb-1">Available time slot</label>
            <CrmSlotPicker
              token={token}
              selectedStart={draft.start_time}
              selectedEnd={draft.end_time}
              onSelect={(slot) => setDraft((d) => ({
                ...d,
                start_time: slot.start_time,
                end_time: slot.end_time,
                timezone: slot.timezone || '',
              }))}
            />
            {selectedLabel ? (
              <p className="mt-2 text-[11px] text-teal-300" data-testid="crm-booking-selected-slot">
                Selected: {selectedLabel}
              </p>
            ) : null}
          </div>
          <div>
            <label className="block text-zinc-400 mb-1">Notes</label>
            <textarea rows={3} value={draft.notes} onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))} className="w-full rounded-md bg-zinc-950 border border-zinc-700 px-3 py-2 text-[11px] resize-none" data-testid="crm-booking-notes-input" />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-1">
          <button onClick={onClose} className="px-3 py-1.5 rounded-md text-xs border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500" data-testid="crm-booking-cancel-button">Cancel</button>
          <button onClick={onSave} disabled={saving || !draft.start_time} className="px-4 py-1.5 rounded-md text-xs font-medium bg-teal-500/20 border border-teal-500/40 text-teal-200 hover:bg-teal-500/30 disabled:opacity-40 inline-flex items-center gap-1.5" data-testid="crm-booking-save-button">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
            {saving ? 'Creating…' : 'Create appointment'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CrmDashboard;
